"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import {
  type Design,
  RiskSummary,
} from "@/components/tools/stats-research/risk-results";
import { TwoByTwoInput } from "@/components/tools/stats-research/two-by-two-input";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { parseIntegerInput } from "@/lib/parse-number";
import { riskMeasures } from "@/lib/tool-kit/stats-research/risk";

/** English strings. A translated content file overrides these through `ui`. */
export const ODDS_RATIO_CALCULATOR_STRINGS = {
  designLabel: "Study design",
  designCohort: "Cohort or trial",
  designCaseControl: "Case-control",
  designCrossSectional: "Cross-sectional",
  tableCaption:
    "Rows: exposure or treatment. Columns: outcome. Enter whole-number counts without thousands separators.",
  outcomeYes: "Outcome",
  outcomeNo: "No outcome",
  caseColumn: "Cases (outcome)",
  controlColumn: "Controls (no outcome)",
  exposed: "Exposed",
  unexposed: "Unexposed",
  total: "Total",
  cellA: "Exposed, with the outcome (a)",
  cellB: "Exposed, without the outcome (b)",
  cellC: "Unexposed, with the outcome (c)",
  cellD: "Unexposed, without the outcome (d)",
  loadExample: "Load example",
  clear: "Clear",
  enterPrompt:
    "Enter the four counts — whole numbers, zero or more — to see the odds ratio, risk ratio and risk difference.",
  invalidCounts: "Each cell must be a whole number, zero or more.",
  emptyMargin:
    "A whole row or column of the table is zero, so the ratios cannot be estimated.",
  results: "Results",
  columnMeasure: "Measure",
  columnEstimate: "Estimate",
  columnInterval: "95% CI",
  columnP: "p (Wald)",
  oddsRatio: "Odds ratio (OR)",
  riskRatio: "Risk ratio (RR)",
  riskDifference: "Risk difference (RD)",
  riskExposed: "Risk in the exposed, a/(a + b)",
  riskUnexposed: "Risk in the unexposed, c/(c + d)",
  prevalenceOddsRatio: "Prevalence odds ratio (POR)",
  prevalenceRatio: "Prevalence ratio (PR)",
  prevalenceDifference: "Prevalence difference",
  prevalenceExposed: "Prevalence in the exposed",
  prevalenceUnexposed: "Prevalence in the unexposed",
  caseControlNote:
    "In a case-control study you choose how many cases and controls to sample, so the share with the outcome in each row reflects your sampling, not the population. Risks, the risk ratio and the risk difference cannot be estimated; the odds ratio can, because it is the same whether you read the table by rows or by columns. When the outcome is rare, the OR approximates the risk ratio.",
  crossSectionalNote:
    "Cross-sectional data give prevalences, not risks: the ratio is a prevalence ratio, the odds ratio a prevalence odds ratio, and neither shows which came first, exposure or outcome.",
  orCorrected:
    "A cell is zero, so 0.5 was added to every cell to estimate the odds ratio and its interval (the Haldane–Anscombe correction).",
  rrCorrected:
    "a or c is zero, so 0.5 was added to every cell to estimate the ratio of risks and its interval.",
  nntHeading: "Number needed to treat (NNT)",
  nntMore:
    "1 / |RD| = {value}: about {rounded} people must be exposed for one more person to have the outcome — a number needed to harm (NNH) when the outcome is bad.",
  nntFewer:
    "1 / |RD| = {value}: about {rounded} people must be exposed for one person fewer to have the outcome — a number needed to treat for benefit (NNTB) when the outcome is bad.",
  nntNone: "The two risks are equal, so the NNT is infinite.",
  nntBounded:
    "95% CI {lower} to {upper}, the reciprocals of the risk difference's limits.",
  nntSplit:
    "The risk difference's interval includes zero, so the NNT interval runs from {benefit} (one outcome fewer) through infinity to {harm} (one outcome more): the data fit benefit, no effect and harm.",
  howCalculated: "How this was calculated",
  workingOr: "OR = (a × d) / (b × c) = ({a} × {d}) / ({b} × {c}) = {value}",
  workingOrSe:
    "SE(ln OR) = √(1/a + 1/b + 1/c + 1/d) = {se}; 95% CI = exp(ln OR ± 1.96 × SE) = {lower} to {upper}",
  workingRr:
    "RR = [a/(a + b)] / [c/(c + d)] = {r1} / {r0} = {value}; SE(ln RR) = √(1/a − 1/(a + b) + 1/c − 1/(c + d)) = {se}",
  workingRd:
    "RD = {r1} − {r0} = {value}; SE = √(r₁(1 − r₁)/n₁ + r₀(1 − r₀)/n₀) = {se}; 95% CI = RD ± 1.96 × SE",
  reportOr: "OR = {value}, 95% CI [{lower}, {upper}]",
  reportRr: "RR = {value}, 95% CI [{lower}, {upper}]",
  reportPor: "POR = {value}, 95% CI [{lower}, {upper}]",
  reportPr: "PR = {value}, 95% CI [{lower}, {upper}]",
  reportRd: "RD = {value}, 95% CI [{lower}, {upper}]",
  copyResult: "Copy result",
  copied: "Copied!",
} as const;

const EXAMPLE = ["40", "160", "20", "180"] as const;

export default function OddsRatioCalculatorWidget() {
  const strings = useToolUi(ODDS_RATIO_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [design, setDesign] = useState<Design>("cohort");
  const [cells, setCells] = useState<string[]>(["", "", "", ""]);

  const values = cells.map((cell) => parseIntegerInput(cell));
  const entered = cells.every((cell) => cell.trim() !== "");
  const valid = values.every((value) => value !== null && value >= 0);
  const [a, b, c, d] = values.map((value) => value ?? 0);
  const result = entered && valid ? riskMeasures(a, b, c, d) : null;

  const set = (index: number) => (value: string) =>
    setCells((current) =>
      current.map((cell, i) => (i === index ? value : cell))
    );
  const caseControl = design === "caseControl";
  const columnHeads = caseControl
    ? ([strings.caseColumn, strings.controlColumn] as const)
    : ([strings.outcomeYes, strings.outcomeNo] as const);
  const inputs = [
    strings.cellA,
    strings.cellB,
    strings.cellC,
    strings.cellD,
  ].map((label, i) => ({
    id: `or-cell-${i}`,
    label,
    value: cells[i],
    onChange: set(i),
    placeholder: EXAMPLE[i],
  }));

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.designLabel}
        name="or-design"
        onChange={setDesign}
        options={[
          { value: "cohort", label: strings.designCohort },
          { value: "caseControl", label: strings.designCaseControl },
          { value: "crossSectional", label: strings.designCrossSectional },
        ]}
        value={design}
      />
      <TwoByTwoInput
        caption={strings.tableCaption}
        cells={[inputs[0], inputs[1], inputs[2], inputs[3]]}
        columnHeads={columnHeads}
        rowHeads={[strings.exposed, strings.unexposed]}
        totalLabel={strings.total}
        totals={{
          rows: [fmt(a + b), fmt(c + d)],
          columns: [fmt(a + c), fmt(b + d)],
          all: fmt(a + b + c + d),
        }}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() => setCells([...EXAMPLE])}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => setCells(["", "", "", ""])}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {entered && !valid ? (
        <ErrorNotice>{strings.invalidCounts}</ErrorNotice>
      ) : null}
      {result ? (
        <RiskSummary
          design={design}
          fmt={fmt}
          result={result}
          strings={strings}
        />
      ) : null}
      {entered ? null : <Notice>{strings.enterPrompt}</Notice>}
    </div>
  );
}
